/**
 * 履歴の一覧: 過去に食べた食品を、記録したときの値のまま選んでカートに入れる（食事シートの履歴タブ）
 */

import { useInfiniteQuery } from '@tanstack/react-query'
import { Loader2, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

import { getMealItemHistory } from '@/api/services/nutrition'
import type { MealItemHistoryEntry } from '@/api/types/nutrition'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/lib/utils'

import { useLoadMoreOnScroll } from '../hooks/use-load-more-on-scroll'
import { HistoryEntryLastEaten, HistoryEntryName } from './history-entry-parts'

const PAGE_SIZE = 30

type Props = {
  selected: MealItemHistoryEntry[]
  onSelectedChange: (selected: MealItemHistoryEntry[]) => void
  /** 一覧の最大高さ（Tailwind のクラス） */
  listMaxHeight?: string
}

export function MealHistorySection({ selected, onSelectedChange, listMaxHeight = 'max-h-52' }: Props) {
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query.trim())

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['nutrition', 'meal-item-history', debouncedQuery],
    queryFn: ({ pageParam }) => getMealItemHistory(debouncedQuery, PAGE_SIZE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.next_offset : undefined),
    // 記録したばかりの食品がすぐ出るよう、開くたびに取り直す
    staleTime: 0,
  })
  const entries = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data])
  const selectedIds = useMemo(() => new Set(selected.map((e) => e.item_id)), [selected])
  const { scrollRef, sentinelRef } = useLoadMoreOnScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    watchKey: entries.length,
  })

  const handleToggle = (entry: MealItemHistoryEntry) => {
    if (selectedIds.has(entry.item_id)) {
      onSelectedChange(selected.filter((e) => e.item_id !== entry.item_id))
    } else {
      onSelectedChange([...selected, entry])
    }
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="食品名で絞り込み..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-8 pl-8 text-sm"
        />
      </div>

      {isLoading ? (
        <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />
      ) : entries.length === 0 ? (
        <p className="py-2 text-center text-xs text-muted-foreground">
          {debouncedQuery ? '一致する食品がありません' : 'まだ記録がありません'}
        </p>
      ) : (
        <div
          ref={scrollRef}
          className={cn(listMaxHeight, 'space-y-0.5 overflow-y-auto rounded-md border border-border p-1')}
        >
          {entries.map((entry) => (
            <label
              key={entry.item_id}
              className="flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 transition-colors hover:bg-accent/50"
            >
              <Checkbox checked={selectedIds.has(entry.item_id)} onCheckedChange={() => handleToggle(entry)} />
              <div className="min-w-0 flex-1">
                <HistoryEntryName entry={entry} className="block text-sm font-medium" />
                <div className="mt-0.5 flex gap-2 text-[11px] text-muted-foreground">
                  <span className="tabular-nums">{Math.round(entry.calories)}kcal</span>
                  <span className="tabular-nums text-blue-400">P{entry.protein_g.toFixed(0)}</span>
                  <span className="tabular-nums text-yellow-400">F{entry.fat_g.toFixed(0)}</span>
                  <span className="tabular-nums text-green-400">C{entry.carb_g.toFixed(0)}</span>
                  <HistoryEntryLastEaten entry={entry} className="ml-auto" />
                </div>
              </div>
            </label>
          ))}
          <div ref={sentinelRef} className="h-px" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-1.5">
              <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
